import {
  ChangeDetectionStrategy,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { first, map, take, takeUntil, tap } from 'rxjs/operators';

import {
  ActivateDeactivateCommands,
  DhwScheduleModeType,
  DhwTemperatureCommands,
  DhwTemperatureHysteresisCommands,
  DhwTemperatureHysteresisProperties,
  Feature,
  FeatureActionFields,
  FeatureCommands,
  FeatureNames,
  FeaturePropertyKey,
  HeatingConfigurationDhwSensorsEcoModeLeadingCommands,
  HeatingConfigurationDhwSensorsEcoModeLeadingProperties,
  HeatingDhwOperatingModesActiveCommands,
  HeatingDhwOperatingModesActiveProperties,
  HeatingDhwRustSurveillanceAnodeProperties,
  HeatingDhwScheduleModeCommands,
  HeatingDhwScheduleModeProperties,
  IsActiveProperty,
  IsNumberValueProperty,
  IsStringValueProperty,
  ScheduleCommands,
  ScheduleProperties,
} from '@vi-iot/client';

import { ViConfirmDialogService } from '@vi-iot/ui';

import { GTMService } from '@services';
import { DevicesStore, FeatureService, InstallationFeaturesStore, isReady } from '@store';

import { MatTooltip } from '@angular/material/tooltip';
import { matTooltipSetter } from 'src/app/shared/functions/mat-tooltip-setter/mat-tooltip-setter';
import {
  Program,
  ScheduleEntry,
  ScheduleService,
  TimemanagerDialogService,
} from '../../../../../features/timemanager/services';
import { MatSlideToggle } from '@angular/material/slide-toggle';

export interface TabList {
  list: Feature<ScheduleProperties, ScheduleCommands>[];
  showTabs: boolean;
}

export interface EfficientBorderContraints {
  efficientLowerBorder: number;
  efficientUpperBorder: number;
}

@Component({
  selector: 'app-domestic-hot-water-panel',
  templateUrl: './domestic-hot-water-panel.component.html',
  styleUrls: ['./domestic-hot-water-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DomesticHotWaterPanelComponent implements OnInit, OnDestroy {
  @ViewChildren(MatTooltip)
  set matTooltip(queryList: QueryList<MatTooltip>) {
    matTooltipSetter(queryList);
  }

  readonly comfortMode = 'comfort';
  readonly ecoMode = 'eco';
  readonly dhwScheduleFeature = FeatureNames.dhwSchedule;
  readonly dhwScheduleModeType = DhwScheduleModeType;
  readonly featureCommands = FeatureCommands;
  readonly featurePropertyKey = FeaturePropertyKey;

  dhwTemperature$: Observable<Feature<IsNumberValueProperty, DhwTemperatureCommands>>;
  dhwTemperature2$: Observable<Feature<IsNumberValueProperty, DhwTemperatureCommands>>;
  dhwHysteresis$: Observable<
    Feature<DhwTemperatureHysteresisProperties, DhwTemperatureHysteresisCommands>
  >;
  dhwMinimumComfort$: Observable<Feature<any, never>>;
  dhwOneTimeCharge$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;
  dhwSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  circulationSchedule$: Observable<Feature<ScheduleProperties, ScheduleCommands>>;
  isMultiFamily$: Observable<Feature<IsActiveProperty, never>>;
  isDhwOneTimeChargeEnabled$: Observable<boolean>;
  dhwEcoModeLeading$: Observable<
    Feature<
      HeatingConfigurationDhwSensorsEcoModeLeadingProperties,
      HeatingConfigurationDhwSensorsEcoModeLeadingCommands
    >
  >;
  dhwTemperatureComfortCharging$: Observable<Feature<IsNumberValueProperty, never>>;
  storageTankSensorTop$: Observable<Feature<IsNumberValueProperty, never>>;
  storageTankSensorMiddle$: Observable<Feature<IsNumberValueProperty, never>>;
  dhwHotWaterStorageTemperatureMidBottom$: Observable<Feature<IsNumberValueProperty, never>>;
  dhwHotWaterStorageTemperatureBottom$: Observable<Feature<IsNumberValueProperty, never>>;

  bufferSensorTemperature$: Observable<Feature<IsNumberValueProperty, never>>;
  bufferSensorTemperatureTop$: Observable<Feature<IsNumberValueProperty, never>>;
  bufferSensorTemperatureMiddle$: Observable<Feature<IsNumberValueProperty, never>>;
  bufferSensorTemperatureMidBottom$: Observable<Feature<IsNumberValueProperty, never>>;
  bufferSensorTemperatureBottom$: Observable<Feature<IsNumberValueProperty, never>>;

  dhwActiveOperatingMode$: Observable<
    Feature<HeatingDhwOperatingModesActiveProperties, HeatingDhwOperatingModesActiveCommands>
  >;
  dhwScheduleMode$: Observable<
    Feature<HeatingDhwScheduleModeProperties, HeatingDhwScheduleModeCommands>
  >;
  dhwRustSurveillanceAnode$: Observable<Feature<HeatingDhwRustSurveillanceAnodeProperties, never>>;
  offPeakSignalActive$: Observable<Feature<IsActiveProperty, ActivateDeactivateCommands>>;

  dhwCurrentTemperature: number;
  dhwPreviousTemperature: number;
  dhw2CurrentTemperature: number;
  dhw2PreviousTemperature: number;
  dhwEcoModeLeading: string;
  dhwActiveOperatingMode: string;
  dhwActiveScheduleMode: string;
  editMultipleDays: boolean;
  activeTab: string;
  tabs$: Observable<TabList>;
  programs$: BehaviorSubject<Program[]> = new BehaviorSubject<Program[]>([]);
  // workaround for canceling mat-select behaviour
  dhwTemperatureSelectVisible$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  actuatorForDhwPreparation$: Observable<Feature<IsStringValueProperty, never>>;

  private dhwEfficiencyModalContent = {
    title: this.translateService.instant('+INSTALLATION.DHW.EFFICIENCY_MODAL.TITLE'),
    content: this.translateService.instant('+INSTALLATION.DHW.EFFICIENCY_MODAL.DESCRIPTION'),
    confirmBtn: this.translateService.instant('GENERAL.BUTTON.CONFIRM'),
    cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
  };

  private ngUnsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public devicesStore: DevicesStore,
    private featureService: FeatureService,
    private scheduleService: ScheduleService,
    private timemanagerDialogService: TimemanagerDialogService,
    private gtmService: GTMService,
    private translateService: TranslateService,
    private viConfirmModalService: ViConfirmDialogService,
    private installationFeaturesStore: InstallationFeaturesStore
  ) {}

  ngOnInit(): void {
    this.dhwTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwMainTemperature
    );
    this.dhwTemperature2$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwTemp2Temperature
    );

    this.dhwTemperature$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((feature) => {
      this.dhwCurrentTemperature = feature?.properties?.value?.value;
      this.dhwPreviousTemperature = this.dhwCurrentTemperature;
    });
    this.dhwTemperature2$.pipe(takeUntil(this.ngUnsubscribe)).subscribe((feature) => {
      this.dhw2CurrentTemperature = feature?.properties?.value?.value;
      this.dhw2PreviousTemperature = this.dhw2CurrentTemperature;
    });

    this.dhwMinimumComfort$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwMinimumComfort
    );

    this.dhwHysteresis$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwTemperatureHysteresis
    );

    this.dhwOneTimeCharge$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwOneTimeCharge
    );

    // Timemanager for DHW
    this.dhwSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwSchedule
    );
    this.circulationSchedule$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwPumpsCirculationSchedule
    );

    this.isMultiFamily$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.configurationMultiFamilyHouse
    );
    this.dhwTemperatureComfortCharging$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwTemperatureComfortCharging
    );
    this.storageTankSensorTop$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorTop
    );
    this.storageTankSensorMiddle$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorMiddle
    );

    this.bufferSensorTemperature$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensor
    );

    this.bufferSensorTemperatureTop$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorTop
    );

    this.bufferSensorTemperatureMiddle$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorMiddle
    );

    this.bufferSensorTemperatureMidBottom$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorMidBottom
    );

    this.bufferSensorTemperatureBottom$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.storageTankSensorBottom
    );

    // Anode control
    this.dhwRustSurveillanceAnode$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.dhwRustSurveillanceAnode
    );

    this.offPeakSignalActive$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.deviceOffPeakSignalConfiguration
    );

    this.actuatorForDhwPreparation$ = this.installationFeaturesStore.selectInstallationDeviceFeature(
      FeatureNames.heatingDhwActuator
    );

    this.isDhwOneTimeChargeEnabled$ = this.featureService.selectIsDhwOneTimeChargeEnabled();

    this.programs$ = this.scheduleService.programs$;
    this.tabs$ = combineLatest([this.dhwSchedule$, this.circulationSchedule$]).pipe(
      map((data) => {
        const tabs = data.filter((feature) => isReady(feature));

        const tabList: TabList = {
          list: tabs,
          showTabs: !!tabs.length,
        };

        return tabList;
      })
    );

    this.dhwActiveOperatingMode$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        HeatingDhwOperatingModesActiveProperties,
        HeatingDhwOperatingModesActiveCommands
      >(FeatureNames.dhwActiveOperatingMode)
      .pipe(
        tap((feature) => {
          this.dhwActiveOperatingMode = feature?.properties?.value?.value;
        })
      );

    this.dhwScheduleMode$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        HeatingDhwScheduleModeProperties,
        HeatingDhwScheduleModeCommands
      >(FeatureNames.dhwScheduleMode)
      .pipe(
        tap((feature) => {
          this.dhwActiveScheduleMode = feature?.properties?.value?.value;
        })
      );

    this.dhwEcoModeLeading$ = this.installationFeaturesStore
      .selectInstallationDeviceFeature<
        HeatingConfigurationDhwSensorsEcoModeLeadingProperties,
        HeatingConfigurationDhwSensorsEcoModeLeadingCommands
      >(FeatureNames.dhwEcoModeLeading)
      .pipe(
        tap((feature) => {
          this.dhwEcoModeLeading = feature?.properties?.value?.value;
        })
      );

    this.scheduleService.isDhwSchedule$.next(true);
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
    this.scheduleService.isDhwSchedule$.next(false);
  }

  initTabs(features: Feature<ScheduleProperties, ScheduleCommands>[]): boolean {
    if (!this.activeTab) {
      if (features && isReady(features[0]) && !isEmpty(features[0].commands)) {
        this.onTabChange(features[0]);
      } else if (features && isReady(features[1]) && !isEmpty(features[1].commands)) {
        this.onTabChange(features[1]);
      } else {
        this.editMultipleDays = false;

        return false;
      }
    } else {
      const feature = features.find((f) => f.feature === this.activeTab);
      this.onTabChange(feature);
    }
    this.editMultipleDays = true;

    return true;
  }

  onEditMultipleDays(): void {
    this.timemanagerDialogService.openSelectDaysDialog();
  }

  onTabChange(feature: Feature<ScheduleProperties, ScheduleCommands>): void {
    const scheduleFeature = cloneDeep(feature);
    this.activeTab = scheduleFeature?.feature;
    if (!this.activeTab) {
      return;
    }
    this.scheduleService.createSchedule(scheduleFeature);
    const modes = this.featureService.getScheduleModes(scheduleFeature);
    this.scheduleService.createModes(modes);
  }

  onDhwTemperatureChange(temperatureValue: number): void {
    this.dhwTemperature$
      .pipe(first())
      .subscribe((feature: Feature<IsNumberValueProperty, DhwTemperatureCommands>) => {
        const isEfficientTemperature = this.isEfficientTemperature(feature, temperatureValue);

        if (isEfficientTemperature) {
          this.openEfficientTemperatureConfirmModal(temperatureValue);
        } else {
          this.dhwPreviousTemperature = this.dhwCurrentTemperature;
          this.dhwCurrentTemperature = temperatureValue;
          this.setDhwTemperature(temperatureValue);
        }
      });
  }

  onDhwTemperature2Change(temperatureValue: number): void {
    this.dhwTemperature2$
      .pipe(first())
      .subscribe((feature: Feature<IsNumberValueProperty, DhwTemperatureCommands>) => {
        const isEfficientTemperature = this.isEfficientTemperature(feature, temperatureValue);

        if (isEfficientTemperature) {
          this.openEfficientTemperature2ConfirmModal(temperatureValue);
        } else {
          this.dhw2PreviousTemperature = this.dhw2CurrentTemperature;
          this.dhw2CurrentTemperature = temperatureValue;
          this.setDhw2Temperature(temperatureValue);
        }
      });
  }

  onDhwScheduleModeChanged(option: { value: string }): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwScheduleMode,
      option.value,
      this.featurePropertyKey.value,
      FeatureActionFields.mode,
      FeatureCommands.setMode
    );
  }

  onDhwOperatingModeChanged(option: { value: string }): void {
    const operatingModeChanged = option.value !== this.dhwActiveOperatingMode;
    if (operatingModeChanged) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.dhwActiveOperatingMode,
        option.value,
        this.featurePropertyKey.value,
        FeatureActionFields.mode,
        FeatureCommands.setMode
      );
    }
  }

  onDhwEcoModeLeadingChanged(option: { value: string }): void {
    const ecoModeChanged = option.value !== this.dhwEcoModeLeading;
    if (ecoModeChanged) {
      this.installationFeaturesStore.updateFeature(
        FeatureNames.dhwEcoModeLeading,
        option.value,
        this.featurePropertyKey.value,
        '',
        FeatureCommands.setValue
      );
    }
  }

  private openEfficientTemperatureConfirmModal(temperatureValue: number): void {
    this.viConfirmModalService
      .open(this.dhwEfficiencyModalContent)
      .pipe(first())
      .subscribe((conditionAccepted) => {
        if (conditionAccepted) {
          this.setDhwTemperature(temperatureValue);
          this.dhwPreviousTemperature = this.dhwCurrentTemperature;
          this.dhwCurrentTemperature = temperatureValue;
        }

        this.forceRefreshMaterialSelect();
      });
  }

  private openEfficientTemperature2ConfirmModal(temperatureValue: number): void {
    this.viConfirmModalService
      .open(this.dhwEfficiencyModalContent)
      .pipe(first())
      .subscribe((conditionAccepted) => {
        if (conditionAccepted) {
          this.setDhw2Temperature(temperatureValue);
          this.dhw2PreviousTemperature = this.dhw2CurrentTemperature;
          this.dhw2CurrentTemperature = temperatureValue;
        }

        this.forceRefreshMaterialSelect();
      });
  }

  onDhwHysteresisChange(value: number, featureCommands: string): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwTemperatureHysteresis,
      value,
      this.featurePropertyKey.value,
      FeatureActionFields.hysteresis,
      featureCommands
    );
    this.gtmService.track('onSet', 'Domestic hot water hysteresis', 'switchingTimes');
  }

  onChangeMinimumComfortTemperature(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwMinimumComfort,
      value,
      this.featurePropertyKey.value,
      FeatureActionFields.temperature,
      FeatureCommands.setTemperature
    );
    this.gtmService.track('onSet', 'Domestic hot water minimum comfort temperature', 'dhw');
  }

  onDhwTemperatureComfortChange(value: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwTemperatureComfortCharging,
      value,
      this.featurePropertyKey.value,
      FeatureActionFields.temperature,
      FeatureCommands.setTemperature
    );
    this.gtmService.track('onSet', 'Domestic hot water temperature comfort charging', 'dhw');
  }

  onDhwOneTimeChargeChange(value: boolean): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwOneTimeCharge,
      value,
      this.featurePropertyKey.active,
      '',
      value ? FeatureCommands.activate : FeatureCommands.deactivate
    );
    this.gtmService.track(value ? 'onOnOneTimeCharge' : 'onOffOneTimeCharge');
  }

  onOffPeakSignalConfigurationChange(
    enabledToggle: MatSlideToggle,
    switchToEnabled: boolean
  ): void {
    enabledToggle.toggle();

    this.viConfirmModalService
      .open({
        title: this.translateService.instant(
          switchToEnabled
            ? this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.OFF_PEAK_SIGNAL.ACTIVATE_TITLE'
              )
            : this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.OFF_PEAK_SIGNAL.DEACTIVATE_TITLE'
              )
        ),
        content: this.translateService.instant(
          switchToEnabled
            ? this.translateService.instant('+INSTALLATIONS.INSTALLATION.OFF_PEAK_SIGNAL.ACTIVATE')
            : this.translateService.instant(
                '+INSTALLATIONS.INSTALLATION.OFF_PEAK_SIGNAL.DEACTIVATE'
              )
        ),
        confirmBtn: this.translateService.instant('GENERAL.BUTTON.CONFIRM'),
        cancelBtn: this.translateService.instant('GENERAL.BUTTON.CANCEL'),
      })
      .pipe(take(1))
      .subscribe((conditionAccepted) => {
        if (conditionAccepted) {
          this.installationFeaturesStore.updateFeature(
            FeatureNames.deviceOffPeakSignalConfiguration,
            true,
            this.featurePropertyKey.active,
            '',
            switchToEnabled ? FeatureCommands.activate : FeatureCommands.deactivate
          );
        }
      });
  }

  onScheduleChange(
    event: { scheduleIds: number[]; periods: ScheduleEntry[] },
    features: Feature<ScheduleProperties, ScheduleCommands>[]
  ): void {
    const feature = cloneDeep(features.find((f) => f.feature === this.activeTab));
    const schedule = this.scheduleService.getScheduleEntries(feature);
    const value = this.scheduleService.getScheduleValue(event.scheduleIds, event.periods, schedule);
    this.installationFeaturesStore.updateFeature(
      this.activeTab,
      cloneDeep(value),
      this.featurePropertyKey.entries,
      FeatureActionFields.newSchedule,
      FeatureCommands.setSchedule
    );
  }

  private setDhwTemperature(temperatureValue: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwMainTemperature,
      temperatureValue,
      this.featurePropertyKey.value,
      FeatureActionFields.temperature,
      FeatureCommands.setTargetTemperature
    );
    this.gtmService.track('onSet', 'Domestic hot water temperature', 'switchingTimes');
  }

  private setDhw2Temperature(temperatureValue: number): void {
    this.installationFeaturesStore.updateFeature(
      FeatureNames.dhwTemp2Temperature,
      temperatureValue,
      this.featurePropertyKey.value,
      FeatureActionFields.temperature,
      FeatureCommands.setTargetTemperature
    );
    this.gtmService.track('onSet', 'Domestic hot water temperature 2', 'switchingTimes');
  }

  private isEfficientTemperature(
    feature: Feature<IsNumberValueProperty, DhwTemperatureCommands>,
    temperatureValue: number
  ): boolean {
    const constraints = this.getEfficientBorders(feature);
    const isTemperatureValueAbove = temperatureValue > constraints.efficientUpperBorder;
    const isTemperatureValueBelow = temperatureValue < constraints.efficientLowerBorder;

    return isTemperatureValueAbove || isTemperatureValueBelow;
  }

  private getEfficientBorders(
    feature: Feature<IsNumberValueProperty, DhwTemperatureCommands>
  ): EfficientBorderContraints {
    const constraints = feature?.commands?.setTargetTemperature?.params?.temperature?.constraints;

    return {
      efficientLowerBorder: constraints.efficientLowerBorder,
      efficientUpperBorder: constraints.efficientUpperBorder,
    };
  }

  private forceRefreshMaterialSelect(): void {
    this.dhwTemperatureSelectVisible$.next(false);
    setTimeout(() => {
      this.dhwTemperatureSelectVisible$.next(true);
    });
  }
}
